import { ArrowRight } from "lucide-react";
import { type RefObject, useEffect, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
  frameRef: RefObject<HTMLElement>;
  // Where the fixed bar is rendered: the page root that holds the colour tokens. Outside the
  // frame, whose entrance animation (a transform) would otherwise pin it to the frame.
  hostSelector: string;
  // "Step 1 of 4 · Send the interview"
  step: string;
};

type Target = { element: HTMLElement; label: string; disabled: boolean };

const readTarget = (frame: HTMLElement): Target | null => {
  const element = frame.querySelector<HTMLElement>("[data-next-step]");
  if (!element) return null;
  return {
    element,
    label: element.dataset.nextStep || element.textContent?.trim() || "",
    disabled: element.matches(":disabled"),
  };
};

const sameTarget = (a: Target | null, b: Target | null) =>
  a?.element === b?.element && a?.label === b?.label && a?.disabled === b?.disabled;

/**
 * On a phone the demo is taller than the screen, so the button for the next step is often out
 * of sight. While the demo is on screen and that button isn't, a bar at the bottom of the screen
 * names the step and offers the same button. The step's main button is marked with
 * `data-next-step` (its value, when set, is a shorter label for the bar). Hidden above 640px.
 */
export const MobileNextStep = ({ frameRef, hostSelector, step }: Props) => {
  const [target, setTarget] = useState<Target | null>(null);
  const [frameInView, setFrameInView] = useState(false);
  const [targetInView, setTargetInView] = useState(true);

  // The marked button changes as the steps animate in and out.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const refresh = () => {
      const next = readTarget(frame);
      setTarget((current) => (sameTarget(current, next) ? current : next));
    };
    refresh();
    const observer = new MutationObserver(refresh);
    observer.observe(frame, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "data-next-step"],
    });
    return () => observer.disconnect();
  }, [frameRef]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof IntersectionObserver === "undefined") return;
    // Counts once the demo reaches the top three quarters of the screen.
    const observer = new IntersectionObserver(([entry]) => setFrameInView(entry.isIntersecting), {
      rootMargin: "0px 0px -25% 0px",
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frameRef]);

  const element = target?.element;
  useEffect(() => {
    if (!element || typeof IntersectionObserver === "undefined") return;
    // The bar covers the bottom of the screen, so a button behind it isn't "in view".
    const observer = new IntersectionObserver(
      ([entry]) => setTargetInView(entry.intersectionRatio >= 0.6),
      { rootMargin: "0px 0px -88px 0px", threshold: [0, 0.6, 1] }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  if (!target || target.disabled || !frameInView || targetInView) return null;

  const goToStep = () => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    target.element.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
    target.element.click();
  };

  const host = frameRef.current?.closest<HTMLElement>(hostSelector) ?? document.body;
  return createPortal(
    <div className="a25-next-bar" role="region" aria-label="Next step">
      <span className="a25-next-bar-step">{step}</span>
      <button type="button" className="btn" onClick={goToStep}>
        {target.label} <ArrowRight size={16} aria-hidden="true" />
      </button>
    </div>,
    host
  );
};
